/// <amd-module name="TFS/Views/ProjectOverviewPage/ProjectOverviewTab/ProjectOverviewTab" />
import * as React from "react";

import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, IVssComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";

import { AboutProjectActionCreator } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Actions/AboutProjectActionCreator";
import { ActionsHub } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Actions/ActionsHub";
import { ProjectOverviewTabActionCreator } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Actions/ProjectOverviewTabActionCreator";
import { AboutProject } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/AboutProject/AboutProject";
import { ProjectOverviewDayZeroComponent } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/ProjectOverviewDayZeroComponent";
import { StatsSection } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/StatsSection";
import { ProjectMembers } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/MembersRegion/ProjectMembers";
import { AboutProjectData, Permissions } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Models";
import { StoresHub } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Stores/StoresHub";
import { getFeatureFlagState } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Utils";
import { CurrentUserInfoStore } from "TFS/Views/ProjectOverviewPage/Shared/CurrentUserInfoStore";
import { ProjectBasicInfoStore } from "TFS/Views/ProjectOverviewPage/Shared/ProjectBasicInfoStore";
import { ProjectMembersActionCreator } from "TFS/Views/ProjectOverviewPage/Shared/ProjectMembersActionCreator";
import { ProjectMembersStore } from "TFS/Views/ProjectOverviewPage/Shared/ProjectMembersStore";

interface ProjectOverviewTabProps extends IVssComponentProperties {
    projectBasicInfoStore: ProjectBasicInfoStore;
    currentUserInfoStore: CurrentUserInfoStore;
    projectMembersActionCreator: ProjectMembersActionCreator;
    projectMembersStore: ProjectMembersStore;
}

interface ProjectOverviewTabState {
    permissions: Permissions;
    showDayZeroComponent: boolean;
}

class ProjectOverviewTab extends VssComponent<ProjectOverviewTabProps, ProjectOverviewTabState> {
    private _storesHub: StoresHub;
    private _aboutProjectActionCreator: AboutProjectActionCreator;
    private _projectOverviewTabActionCreator: ProjectOverviewTabActionCreator;
    private _performance: IVssPerformanceService;

    constructor(props: ProjectOverviewTabProps, context: IVssComponentContext) {
        super(props, context);

        this._performance = context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._performance.addSplitTiming("ProjectOverviewTab.render.Start");

        // Initialize flux
        const actionsHub = new ActionsHub();
        this._storesHub = new StoresHub(actionsHub, props.projectBasicInfoStore, props.currentUserInfoStore);
        this._aboutProjectActionCreator = new AboutProjectActionCreator(context.pageContext, actionsHub, this._storesHub);
        this._projectOverviewTabActionCreator = new ProjectOverviewTabActionCreator(context.pageContext, actionsHub);

        const useIsAnyServiceInitializedData = getFeatureFlagState(
            this.context.pageContext,
            "WebAccess.Project.Verticals.OverviewPage.UseIsAnyServiceInitializedData"
        );

        let showDayZeroComponent = true;
        const contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");
        if (useIsAnyServiceInitializedData) {
            const data = contributionService.getData<{ aboutProjectData: AboutProjectData }>("ms.vss-tfs-web.about-project-data-provider-verticals");
            showDayZeroComponent = !!data && data.aboutProjectData && !data.aboutProjectData.isAnyServiceInitialized;
        } else {
            const initializedServices = contributionService.getContributionChildren(
                "ms.vss-tfs-web.project-overview-tab-data",
                "ms.vss-tfs-web.service-status-info"
            );
            showDayZeroComponent = !!initializedServices && initializedServices.length === 0;
        }

        this.state = {
            permissions: this._storesHub.permissionsStore.getState().permissions,
            showDayZeroComponent
        };
    }

    public render(): JSX.Element {
        const { permissions, showDayZeroComponent } = this.state;
        const { projectMembersActionCreator, projectMembersStore } = this.props;

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="project-overview-tab flex flex-noshrink absolute-fill">
                    <div className="flex overview-region">
                        <div className="left-region">
                            {showDayZeroComponent ? (
                                <ProjectOverviewDayZeroComponent
                                    className="po-day-zero"
                                    currentUserInfoStore={this._storesHub.currentUserInfoStore}
                                />
                            ) : (
                                <AboutProject actionCreator={this._aboutProjectActionCreator} storesHub={this._storesHub} />
                            )}
                        </div>
                        <div className="right-region">
                            <WrappedComponent
                                dependencies={["ms.vss-tfs-web.project-overview-tab-stats-section"]}
                                wrappedType={"TFS.ProjectOverviewPage.ProjectOverviewTab.RightSection"}
                                wrappedProps={{
                                    permissions,
                                    projectMembersActionCreator,
                                    projectMembersStore
                                }}
                            />
                        </div>
                    </div>
                </Page>
            </Surface>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this._performance.addSplitTiming("ProjectOverviewTab.render.End");

        this._storesHub.permissionsStore.addChangedListener(this._onPermissionChange);

        this._aboutProjectActionCreator.loadInitialData();

        // loadPermissions will trigger a flux action. However this component is likely being rendered from
        // the projectOverviewPageLoaded action, and you can't invoke an action from within the handler of another flux action.
        this.setTimeout(() => {
            this._projectOverviewTabActionCreator.loadPermissions();
        }, 0);
    }

    public componentWillUnmount(): void {
        this._storesHub.permissionsStore.removeChangedListener(this._onPermissionChange);

        super.componentWillUnmount();
    }

    private _onPermissionChange = (): void => {
        this.setState({ permissions: this._storesHub.permissionsStore.getState().permissions });
    };
}

VssComponent.register("TFS.ProjectOverviewPage.ProjectOverviewTab", ProjectOverviewTab);

interface RightSectionProps {
    permissions: Permissions;
    projectMembersActionCreator: ProjectMembersActionCreator;
    projectMembersStore: ProjectMembersStore;
}

class RightSection extends VssComponent<RightSectionProps, {}> {
    public render(): JSX.Element {
        const { permissions, projectMembersActionCreator, projectMembersStore } = this.props;

        return (
            <>
                <StatsSection />
                {permissions.hasViewActivityPanePermission ? (
                    <ProjectMembers
                        className="project-overview-card"
                        headingLevel={2}
                        actionCreator={projectMembersActionCreator}
                        store={projectMembersStore}
                    />
                ) : null}
            </>
        );
    }
}

VssComponent.register("TFS.ProjectOverviewPage.ProjectOverviewTab.RightSection", RightSection);
